import type { CSSProperties, RefObject } from "react";
import { useLayoutEffect, useRef, useState } from "react";

export type PopoverPlacement = "bottom" | "top" | "right" | "left";

function fits(spacePreferred: number, spaceOpposite: number, needed: number, offset: number) {
  return spacePreferred >= needed + offset || spacePreferred >= spaceOpposite;
}

export interface UsePopoverPositionArgs<T extends HTMLElement = HTMLElement> {
  isOpen: boolean;

  anchorRef?: RefObject<HTMLElement | null>;

  anchorPoint?: { x: number; y: number };

  placement?: PopoverPlacement;

  align?: "start" | "center";

  offset?: number;

  matchAnchorWidth?: boolean;

  /** Caller-provided panel ref, for when another hook (useListbox) must reference the same panel. */
  panelRef?: RefObject<T | null>;
}

export function usePopoverPosition<T extends HTMLElement = HTMLElement>({
  isOpen,
  anchorRef,
  anchorPoint,
  placement = "bottom",
  align,
  offset = 4,
  matchAnchorWidth = false,
  panelRef: panelRefProp,
}: UsePopoverPositionArgs<T>) {
  const internalPanelRef = useRef<T>(null);
  // Same return type as before (React 18 refs reject RefObject<T | null> in `ref=`).
  const panelRef = (panelRefProp ?? internalPanelRef) as RefObject<T>;
  const [style, setStyle] = useState<CSSProperties>({ position: "fixed", top: 0, left: 0, visibility: "hidden" });

  useLayoutEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const reposition = () => {
      const anchor = anchorRef?.current
        ? anchorRef.current.getBoundingClientRect()
        : anchorPoint
          ? {
              top: anchorPoint.y,
              bottom: anchorPoint.y,
              left: anchorPoint.x,
              right: anchorPoint.x,
              width: 0,
              height: 0,
            }
          : null;
      if (!anchor) return;

      const { innerWidth, innerHeight } = window;
      const { width, height } = panel.getBoundingClientRect();

      const clampWidth = matchAnchorWidth && anchorRef?.current ? anchor.width : width;

      const effectivePlacement = anchorRef?.current ? placement : "bottom";
      const resolvedAlign = align ?? (effectivePlacement === "bottom" ? "start" : "center");
      let top: number;
      let left: number;

      if (effectivePlacement === "right" || effectivePlacement === "left") {
        const spaceRight = innerWidth - anchor.right;
        const spaceLeft = anchor.left;
        const placeRight =
          effectivePlacement === "right"
            ? fits(spaceRight, spaceLeft, width, offset)
            : !fits(spaceLeft, spaceRight, width, offset);
        left = placeRight ? anchor.right + offset : Math.max(offset, anchor.left - width - offset);
        top = resolvedAlign === "center" ? anchor.top + anchor.height / 2 - height / 2 : anchor.top;
      } else {
        const spaceBelow = innerHeight - anchor.bottom;
        const spaceAbove = anchor.top;
        const placeBelow =
          effectivePlacement === "bottom"
            ? fits(spaceBelow, spaceAbove, height, offset)
            : !fits(spaceAbove, spaceBelow, height, offset);
        top = placeBelow ? anchor.bottom + offset : Math.max(offset, anchor.top - height - offset);
        left = resolvedAlign === "center" ? anchor.left + anchor.width / 2 - width / 2 : anchor.left;
      }

      top = Math.min(Math.max(offset, top), innerHeight - height - offset);
      left = Math.min(Math.max(offset, left), innerWidth - clampWidth - offset);

      setStyle({
        position: "fixed",
        top,
        left,
        visibility: "visible",
        ...(matchAnchorWidth && anchorRef?.current ? { width: anchor.width } : {}),
      });
    };

    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    // Content that changes size while the panel stays open (e.g. a tooltip's text) would otherwise
    // leave it at its old position, since nothing above reacts to that on its own.
    const resizeObserver = new ResizeObserver(reposition);
    resizeObserver.observe(panel);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      resizeObserver.disconnect();
    };
  }, [isOpen, anchorRef, anchorPoint, placement, align, offset, matchAnchorWidth, panelRef]);

  return { panelRef, style };
}
